import { useLocation, useParams } from "wouter";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useAttributes, useCreateVersion, useDataTypes, useRuleBind, useUpdateVersion } from "@/hooks/useLocalStorage";
import { Type, Code, RuleVersion } from "../../shared/schema";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { AlertDialog } from "@/widgets/AlertDailog";
import { useQueryClient } from "@tanstack/react-query";
import { ExpressionBuilderModal } from "../ExpressionRow";

import ModiftRule from "./ModiftRule";

import { RuleRow, PricingRow, RuleFormData } from "./UpdateBuilderSchema";
import { buildRuleString, buildPricingString, buildFinalRule } from "../buildRule/RuleBuilderFunctions";
import ModifyPricing from "./ModifyPricing";
import RuleGenerate from "../buildRule/NestedRule/RuleGenerate";
import { useBreadcrumbs } from "../BreadcrumbContext";

export default function ModifyValue() {
    const { ruleId } = useParams();
    const [location, setLocation] = useLocation();
    const queryClient = useQueryClient();

    const methods = useForm<RuleFormData>({
        defaultValues: {
            ruleType: { value: "-1", label: "" },
            ruleExpression: "",
            rows: [{ id: 1, isEditing: false, isNew: false, condition: "If" }],
            NestedRule: {
                groups: [
                    {
                        id: 'group-1',
                        type: 'group',
                        conditions: [
                            {
                                id: 'cond-1',
                                dataType: '',
                                attribute: '',
                                operator: '==',
                                valueType: 'operand',
                                dataType2: '',
                                attribute2: ''
                            }
                        ],
                        logicalOperators: []
                    }
                ],
                groupOperators: []
            },
            NestedRuleString: "",
            pricingRows: [{
                id: 1,
                isEditing: false,
                dataType: "",
                attribute: "",
                operator: "=",
                dataType2: "",
                attribute2: "",
                expressionType: "operand",
                expressionValue: "",
                ExpressionJson: "",
                isNew: false
            }],
            finalRule: "",
            isValidated: false
        }
    });

    const { watch, setValue, register } = methods;
    const rows = watch("rows");
    const NestedRule = watch("NestedRule");
    const NestedRuleStringDa = watch("NestedRuleString");
    const pricingRows = watch("pricingRows");
    const ruleType = watch("ruleType");
    const finalRule = watch("finalRule");
    const remarks = watch("remarks") || "";

    const [dataType, setDataType] = useState<Type[]>([]);
    const [attribute, setAttribute] = useState<Code[]>([]);
    const [attrCode, setAttrCode] = useState("");
    const [showPricingAdjustment, setShowPricingAdjustment] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);
    const [openExpression, setOpenExpression] = useState(false);
    const [expressionData, setExpressionData] = useState<any>(null);
    const [ruleObjValue, setRuleObjValue] = useState<RuleVersion>();
    const [objectId, setObjectId] = useState<number>(0);
    const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

    const { data: existingDtValues } = useDataTypes(1, undefined, undefined);
    const { data: existingAttribute } = useAttributes(attrCode);
    const { data: ruleBindVal } = useRuleBind(ruleId ?? "");

    const updateRuleVersionMutation = useUpdateVersion();
    const saveNewVersionMutation = useCreateVersion();

    let ruleObj = queryClient.getQueryData(["selectedVersionRule"]) as RuleVersion | undefined;

    useEffect(() => {
        if (!ruleObj) {
            const saved = localStorage.getItem("selectedVersionRule");
            if (saved) {
                ruleObj = JSON.parse(saved);
                queryClient.setQueryData(["selectedVersionRule"], ruleObj);
            }
        }
        setRuleObjValue(ruleObj);
    }, [ruleObj]);

    const { pushBreadcrumb } = useBreadcrumbs();

    useEffect(() => {
        pushBreadcrumb({
            label: "RuleValue",
        });
    }, []);

    const ruleTypeOptions = [
        { value: "0", label: "Eligibility Rules" },
        { value: "1", label: "Pricing Rules" },
        { value: "2", label: "Credit Rating Rules" },
        { value: "4", label: "Workflow Entry Conditions" },
        { value: "5", label: "Compliance Rules" },
        { value: "6", label: "Form Rules" },
    ];

    const getRuleTypeLabel = (typeValue: string) => {
        const option = ruleTypeOptions.find((opt) => opt.value === typeValue);
        return option ? option.label : "Unknown";
    };

    useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setDataType(existingDtValues.items);
        }
    }, [existingDtValues]);

    useEffect(() => {
        if (existingAttribute && existingAttribute.length > 0) {
            setAttribute(existingAttribute);
        } else {
            setAttribute([]);
        }
    }, [existingAttribute]);

    useEffect(() => {
        if (ruleObj?.ObjectVersionId) {
            setObjectId(Number(ruleObj?.ObjectVersionId));
        }
    }, [ruleObj])

    useEffect(() => {
        if (ruleBindVal) {

            if (ruleBindVal.RuleType !== undefined) {
                setValue("ruleType", {
                    value: String(ruleBindVal.RuleType),
                    label: getRuleTypeLabel(String(ruleBindVal.RuleType))
                });
            }

            if (ruleBindVal.RawRuleJson !== undefined) {
                setValue("NestedRule", ruleBindVal.RawRuleJson);
            }

            if (ruleBindVal.pricing && ruleBindVal.pricing.length > 0) {
                const mappedPricingRows: PricingRow[] = ruleBindVal.pricing.map((pricing: any, index: number) => ({
                    id: index + 1,
                    isEditing: false,
                    dataType: pricing.Data_Type || "",
                    attribute: pricing.Attribute_Name || "",
                    operator: pricing.Operator?.trim() || "=",
                    dataType2: pricing.Exp_DataType || "",
                    attribute2: pricing.Exp_Attribute || "",
                    expressionType: pricing.Exp_Value ? "value" : "operand",
                    expressionValue: pricing.Exp_Value || "",
                    ExpressionJson: pricing.ExpressionJson || "",
                    isNew: false
                }));
                setValue("pricingRows", mappedPricingRows);
                setShowPricingAdjustment(true);
            }
        }
    }, [ruleBindVal]);

    useEffect(() => {
        const completedPricingRows = pricingRows.filter((row: PricingRow) => !row.isEditing);

        const pricingString = buildPricingString(completedPricingRows);
        const finalRuleString = buildFinalRule(NestedRuleStringDa, pricingString);
        setValue("finalRule", `If${finalRuleString}`);
    }, [NestedRuleStringDa, pricingRows]);

    const buildApiPayload = () => {
        return {
            Rule_Id: parseInt(ruleId ?? "0"),
            Data: finalRule,
            ObjectId: ruleObjValue?.ObjectId!,
            Remarks: remarks
        };
    };

    const handleUpdate = async () => {
        try {
            await updateRuleVersionMutation.mutateAsync({
                version: ruleObjValue?.ObjectVersionId!,
                payload: buildApiPayload(),
            });
        } catch (error) {
            console.error("Error updating rule:", error);
        }
    };

    const handleSaveNewVersion = async () => {
        try {
            const payload = buildApiPayload();
            await saveNewVersionMutation.mutateAsync(payload);
        } catch (error) {
            console.error("Error saving new version:", error);
        }
    };

    const navigateback = () => {
        setAlertOpen(false);
        if (reqStatus) {
            setLocation("/ruleversion");
        }
    };

    const callExpressionValue = (data: PricingRow) => {
        setExpressionData(data)
        setEditingRowIndex(data.id)
    }

    useEffect(() => {
        if (updateRuleVersionMutation.isSuccess === true) {
            setReqStatus(updateRuleVersionMutation.isSuccess);
            setAlertMessage(`Version Updated Successfully!`);
            setAlertOpen(true);
            updateRuleVersionMutation.reset();
        } else if (updateRuleVersionMutation.isError) {
            const raw = updateRuleVersionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updateRuleVersionMutation.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            updateRuleVersionMutation.reset();
        }
    }, [updateRuleVersionMutation]);

    useEffect(() => {
        if (saveNewVersionMutation.isSuccess === true) {
            setReqStatus(saveNewVersionMutation.isSuccess);
            setAlertMessage(`New Version Created Successfully!`);
            setAlertOpen(true);
            saveNewVersionMutation.reset();
        } else if (saveNewVersionMutation.isError) {
            const raw = saveNewVersionMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(saveNewVersionMutation.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            saveNewVersionMutation.reset();
        }
    }, [saveNewVersionMutation]);

    return (
        <FormProvider {...methods}>
            <div className="p-6 space-y-6">
                <h1 className="text-xl font-semibold">Object-Attribute Value Setup</h1>
                <div className="flex items-center justify-between border-b pb-2">
                    <div className="flex">
                        <h2 className="text-l font-semibold mr-2">Rule:</h2>
                        <h2>
                            {ruleId} ({ruleObjValue?.Name})
                        </h2>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLocation("/ruleversion")}
                        className="flex items-center gap-1"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                </div>

                <div className="border bg-white border-gray-200 rounded-lg p-2">
                    <label className="font-medium text-gray-700">Raw Rule</label>
                    <Textarea
                        value={ruleObjValue?.raw_rule}
                        className="w-full md:w-full sm:w-3/4 bg-gray-100 text-gray-800 border border-black rounded-md disabled:text-gray-800 disabled:opacity-100"
                        disabled
                        rows={5}
                    />
                </div>

                {ruleType.label !== "Form Rules" && (
                    <RuleGenerate
                        dataType={dataType}
                        attribute={attribute}
                        setAttrCode={setAttrCode}
                        version={true}
                    />
                )}

                {(ruleType.label === "Pricing Rules" || ruleType.label === "Form Rules") && (
                    <div className="pt-6">
                        <ModifyPricing
                            dataType={dataType}
                            attribute={attribute}
                            setAttrCode={setAttrCode}
                            openExpression={openExpression}
                            setOpenExpression={setOpenExpression}
                            setExpressionData={callExpressionValue}
                        />
                    </div>
                )}

                <div className="border bg-white border-gray-200 rounded-lg p-2">
                    <label className="font-medium text-gray-700">
                        Remarks <span className="text-red-500">*</span>
                    </label>
                    <Textarea
                        {...register("remarks")}
                        placeholder="Enter remarks"
                        className="min-h-[60px] w-2/4 md:w-2/4 sm:w-3/4 text-gray-800 border border-gray-300 rounded-md"
                    />
                </div>

                <div className="border bg-white border-gray-200 rounded-lg p-2">
                    <label className="font-medium text-gray-700">Rules with values</label>
                    <Textarea
                        value={finalRule}
                        className="w-full md:w-full sm:w-3/4 bg-gray-100 text-gray-800 border border-black rounded-md disabled:text-gray-800 disabled:opacity-100"
                        disabled
                        rows={5}
                    />
                </div>

                <div className="flex justify-center items-center gap-4 pt-4">
                    {ruleObjValue?.Version &&
                        <Button
                            variant="outline"
                            onClick={handleUpdate}
                            disabled={updateRuleVersionMutation.isPending || !remarks.trim()}
                        >
                            {updateRuleVersionMutation.isPending ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Updating...
                                </>
                            ) : (
                                "Update Current Version"
                            )}
                        </Button>
                    }
                    <Button
                        onClick={handleSaveNewVersion}
                        disabled={saveNewVersionMutation.isPending || !remarks.trim()}
                    >
                        {saveNewVersionMutation.isPending ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Saving...
                            </>
                        ) : (
                            "Save New Version"
                        )}
                    </Button>
                </div>

                <AlertDialog
                    open={alertOpen}
                    onOpenChange={navigateback}
                    message={alertMessage}
                    status={reqStatus}
                />

                <ExpressionBuilderModal
                    open={openExpression}
                    onClose={() => {
                        setOpenExpression(false)
                        setExpressionData(null);
                    }}
                    initialExpression={expressionData?.expressionValue || ""}
                    initialExpressionJson={expressionData?.ExpressionJson ? JSON.parse(expressionData.ExpressionJson) : null}
                    onSave={({ finalExpression, expressionJson }) => {
                        if (expressionData) {
                            const currentRows = methods.getValues("pricingRows");
                            const updatedRows = currentRows.map((row) =>
                                row.id === expressionData.id
                                    ? {
                                        ...row,
                                        expressionValue: finalExpression,
                                        ExpressionJson: JSON.stringify(expressionJson),
                                    }
                                    : row
                            );
                            setValue("pricingRows", updatedRows);
                        }
                        setOpenExpression(false);
                        setExpressionData(null);
                    }}
                />
            </div>
        </FormProvider>
    );
}